import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiResponse } from '../models/api-response.model';
import { MensagemService } from '../../shared/services/mensagem.service';
import { ApiErrorUtil } from '../../shared/utils/api-error.util';
import { MENSAGENS_PADRAO } from '../../shared/constants/mensagens.const';

export const API_RESPONSE_SILENT = new HttpContextToken<boolean>(() => false);
export const API_RESPONSE_SUCCESS_MESSAGE = new HttpContextToken<string | null>(() => null);

export function apiResponseInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const mensagemService = inject(MensagemService);
  const silent = req.context.get(API_RESPONSE_SILENT);
  const successMessage = req.context.get(API_RESPONSE_SUCCESS_MESSAGE);

  return next(req).pipe(
    map((event) => {
      if (event instanceof HttpResponse) {
        if (req.responseType === 'blob') {
          return event;
        }
        const body = normalizarRespostaApi(event.body as ApiResponse<unknown> | undefined);
        const respostaNormalizada = event.clone({ body });
        if (body?.errors && body.errors.length > 0 && !silent) {
          const mensagens = ApiErrorUtil.extrairDoResponse(body);
          if (mensagens.length > 0) {
            mensagemService.erros(mensagens);
          }
        } else if (successMessage && !silent) {
          mensagemService.sucesso(successMessage);
        }
        return respostaNormalizada;
      }
      return event;
    }),
    catchError((error: unknown) => {
      if (isRespostaBinaria(req, error)) {
        return throwError(() => error);
      }

      if (!silent) {
        const mensagens = ApiErrorUtil.extrairDoHttpError(error);
        if (mensagens.length > 0) {
          mensagemService.erros(mensagens);
        } else if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            mensagemService.erro(MENSAGENS_PADRAO.USUARIO_NAO_AUTENTICADO);
          } else if (error.status === 403) {
            mensagemService.erro(MENSAGENS_PADRAO.USUARIO_SEM_PERMISSAO);
          } else if (error.status === 404) {
            mensagemService.erro(MENSAGENS_PADRAO.REGISTRO_NAO_ENCONTRADO);
          } else {
            mensagemService.erro(MENSAGENS_PADRAO.OPERACAO_INDISPONIVEL);
          }
        } else {
          mensagemService.erro(MENSAGENS_PADRAO.OPERACAO_INDISPONIVEL);
        }
      }
      return throwError(() => error);
    })
  );
}

function normalizarRespostaApi<T>(body: T): T {
  return normalizarValor(body) as T;
}

function normalizarValor(valor: unknown): unknown {
  if (Array.isArray(valor)) {
    return valor.map((item) => normalizarValor(item));
  }

  if (!valor || typeof valor !== 'object') {
    return valor;
  }

  if (ehValorMonetarioEmpacotado(valor)) {
    const empacotado = valor as { source?: unknown; parsedValue?: unknown };
    if (typeof empacotado.parsedValue === 'number') {
      return empacotado.parsedValue;
    }
    if (typeof empacotado.source === 'string' && empacotado.source.trim() !== '') {
      const numero = Number(empacotado.source.replace(/\./g, '').replace(',', '.'));
      return Number.isNaN(numero) ? empacotado.source : numero;
    }
    return empacotado.parsedValue ?? empacotado.source;
  }

  const entradas = Object.entries(valor as Record<string, unknown>);
  return entradas.reduce<Record<string, unknown>>((acumulado, [chave, item]) => {
    acumulado[chave] = normalizarValor(item);
    return acumulado;
  }, {});
}

function ehValorMonetarioEmpacotado(valor: object): boolean {
  const registro = valor as Record<string, unknown>;
  return Object.prototype.hasOwnProperty.call(registro, 'source')
    && Object.prototype.hasOwnProperty.call(registro, 'parsedValue')
    && Object.keys(registro).length <= 3;
}

function isRespostaBinaria(req: HttpRequest<unknown>, error: unknown): boolean {
  if (req.responseType === 'blob') {
    return true;
  }

  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const contentType = error.headers?.get('content-type')?.toLowerCase() ?? '';
  return error.error instanceof Blob || contentType.includes('application/pdf');
}
